import type * as MaplibreGl from "maplibre-gl";

import { clamp, ORBIT_ZOOM } from "@/components/atlas-camera";

/**
 * The two glyphs' art: the aircraft silhouette and the aerodrome symbol, the boxes they are
 * authored in, and the ramp an aircraft is drawn at.
 *
 * Both glyphs are rasterised here, in the ink they are drawn with, rather than with `sdf: true`: the
 * colour is baked into the image, so the resting and the hovered state are two images of one
 * geometry — same shape, same size — and prominence rides `icon-opacity` plus the choice of image,
 * with nothing encoding a distance field per frame.
 *
 * Only the art lives here. What the glyphs mean, where they go and which of them survive the
 * declutter is `atlas-planes.ts` and `atlas-ports.ts`.
 */

/**
 * Drawn size, in CSS pixels at the icon's own 24 px art box. Small enough to read as a distant
 * aircraft rather than as a marker, which is also what keeps it clear of the city names.
 */
const PLANE_ICON_SIZE = 0.75;

/**
 * The size ramp. `ORBIT_ZOOM` is the hero's own zoom, where the glyph is drawn at its authored
 * size, and every level below it draws the glyph smaller, down to a floor at the widest chapter's
 * own zoom — which is where the finale sits on a phone and a tablet.
 *
 * A floor rather than a proportional scale, deliberately: the finale's Earth is a fraction of the
 * opening's diameter, and a glyph that shrank with it would be four pixels of noise nobody can
 * read. The floor buys a lighter globe; what takes the rest of the crowding is MapLibre's own
 * placement, which drops an aircraft that would land on top of another.
 */
const PLANE_ICON_SIZE_FAR = 0.45;
const PLANE_SIZE_FAR_ZOOM = 0.4;

/**
 * The size ramp, read by the resting fleet and by the hover ink alike, so the two can never
 * disagree about how big an aircraft is: smaller as the camera pulls back, down to the floor above,
 * because the finale draws an Earth a fraction of the opening's size and a fleet sized for the
 * opening crowds it.
 */
export const planeSizeRamp = (): MaplibreGl.ExpressionSpecification => [
  "interpolate",
  ["linear"],
  ["zoom"],
  PLANE_SIZE_FAR_ZOOM,
  PLANE_ICON_SIZE_FAR,
  ORBIT_ZOOM,
  PLANE_ICON_SIZE,
];

/**
 * The same ramp as arithmetic, for the code that needs a glyph's size in pixels rather than in a
 * paint expression: the declutter's clearance and the pointer's own hit radius. Written once in the
 * anchors and read twice, so a glyph can never be decluttered against a size it is not drawn at.
 * MapLibre clamps an `interpolate` below its first stop and above its last, which is what the
 * clamp here matches.
 */
export const planeSizeAt = (zoom: number): number =>
  clamp(
    PLANE_ICON_SIZE_FAR +
      ((PLANE_ICON_SIZE - PLANE_ICON_SIZE_FAR) * (zoom - PLANE_SIZE_FAR_ZOOM)) /
        (ORBIT_ZOOM - PLANE_SIZE_FAR_ZOOM),
    PLANE_ICON_SIZE_FAR,
    PLANE_ICON_SIZE,
  );

/** The art box the silhouette is authored in, and the factor it is rasterised at. */
export const PLANE_ART_PX = 24;
export const PLANE_PIXEL_RATIO = 2;

/** The art box an airport glyph is authored in. */
const PORT_ART_PX = 16;
/** Drawn at a fixed size: an airport is a point on the map, not something flying towards one. */
export const PORT_ICON_PX = 15;
export const PORT_ICON_SIZE = PORT_ICON_PX / PORT_ART_PX;

/*!
 * The control-tower geometry below is vendored verbatim from lucide-static v1.47.0
 * (`icons/tower-control.svg`, https://unpkg.com/lucide-static@1.47.0/icons/tower-control.svg),
 * whose licence travels with it:
 *
 * ISC License
 *
 * Copyright (c) 2026 Lucide Icons and Contributors
 *
 * Permission to use, copy, modify, and/or distribute this software for any purpose with or without
 * fee is hereby granted, provided that the above copyright notice and this permission notice appear
 * in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS
 * SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE
 * AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT,
 * NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE
 * OF THIS SOFTWARE.
 */
/**
 * The aerodrome symbol: the air-traffic control tower an aircraft on this map answers to, stroked in
 * the line and round joins of the icon set it is taken from, authored in that set's own 24 px box
 * and rasterised into this module's 16 px one.
 */
const TOWER_ART_PX = 24;
const TOWER_STROKE_PX = 1.6;
const TOWER_GEOMETRY = [
  "M18.2 12.27 20 6H4l1.8 6.27a1 1 0 0 0 .95.73h10.5a1 1 0 0 0 .96-.73Z",
  "M8 13v9",
  "M16 22v-9",
  "m9 6 1 7",
  "m15 6-1 7",
  "M12 6V2",
  "M13 2h-2",
];

/**
 * The airliner, authored in a 24 px box with its nose along −y (up the box), because that is the
 * direction `icon-rotate` draws at 0: the course it is given is degrees clockwise from north
 * (`legBearingDegrees`), and the renderer turns the art clockwise by that many degrees. Nose east
 * in the art would put every aircraft 90° off its own course.
 */
const PLANE_PATH =
  "M12 1.2 L11.1 3.9 L10.8 9.7 L3.4 16.6 L3.6 18 L10.8 14.6 L10.8 19.4 L8.6 21.8 L8.8 22.8 " +
  "L12 22.2 L15.2 22.8 L15.4 21.8 L13.2 19.4 L13.2 14.6 L20.4 18 L20.6 16.6 L13.2 9.7 L12.9 3.9 Z";

/** The silhouette as pixels, in the given ink. */
export const planeImage = (ink: string): ImageData | null => {
  const size = PLANE_ART_PX * PLANE_PIXEL_RATIO;
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return null;
  context.canvas.width = size;
  context.canvas.height = size;
  context.scale(PLANE_PIXEL_RATIO, PLANE_PIXEL_RATIO);
  context.fillStyle = ink;
  context.fill(new Path2D(PLANE_PATH));
  return context.getImageData(0, 0, size, size);
};

/**
 * The aerodrome as pixels, in the given ink: the control tower, stroked in the icon set's own line.
 *
 * A stroked tower rather than a ring: at fifteen pixels a chart ring reads as a blob with a bar in
 * it, while the tower keeps the masts and the canopy that make it an airport on a dark map.
 */
export const portImage = (ink: string): ImageData | null => {
  const size = PORT_ART_PX * PLANE_PIXEL_RATIO;
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return null;
  context.canvas.width = size;
  context.canvas.height = size;
  context.scale(PLANE_PIXEL_RATIO, PLANE_PIXEL_RATIO);
  context.strokeStyle = ink;
  // The art is drawn into a smaller box than it was authored in, so the stroke is widened by the
  // same factor it is about to be shrunk by and lands at its own width in the box actually drawn.
  context.lineWidth = (TOWER_STROKE_PX * TOWER_ART_PX) / PORT_ART_PX;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.scale(PORT_ART_PX / TOWER_ART_PX, PORT_ART_PX / TOWER_ART_PX);
  for (const geometry of TOWER_GEOMETRY) context.stroke(new Path2D(geometry));
  return context.getImageData(0, 0, size, size);
};
